/**
 * EVIDENCE-BASED EVALUATION — the shared contract (4 Oct 2026, migration 0099, docs/ARCHITECTURE_AUDIT.md).
 *
 *   Requirement → versioned ASSESSMENT SET → questions → answers (permanent evidence)
 *     → evaluator attributes evidence (any answer → any competency) → competency results
 *     → CAPABILITY (how strong the evidenced competencies are) + COVERAGE (how much of the set was evidenced)
 *
 * Principles this file enforces in code, not just in prompts:
 *   - Competencies belong to the requirement. A question's planning target never decides what is
 *     scored: the evaluator may cite any answer for any competency of the set.
 *   - Absence of evidence is `insufficient_evidence`, never a low score. It is excluded from
 *     capability and shows up in coverage instead.
 *   - Evidence is quoted. A cited span that does not occur in the answer it names is discarded; a
 *     competency left with no verified evidence is `insufficient_evidence`, whatever the model said.
 *   - Evidence has a strength: direct (they did this), transferable (comparable work elsewhere),
 *     weak (generic or theoretical). Coverage counts only the concrete kinds (direct, transferable); capability sees all scored evidence.
 *   - Every evaluation cites the exact assessment-set version it was judged against; ranking only
 *     compares candidates evaluated against the same version.
 */
import { z } from "zod";
import { INTERVIEW_CLASS_LABEL, interviewClassOf } from "./questionScore.js";
// ── assessment sets ──────────────────────────────────────────────────────────
export const ASSESSMENT_LIMITS = { min: 1, max: 20 };
/** snake_case, like every competency id. Custom ids are minted from the label (customCompetencyId). */
export const assessmentCompetencyIdSchema = z
    .string()
    .regex(/^[a-z][a-z0-9_]{1,63}$/, "Competency ids are lower snake_case, 2-64 characters");
export const assessmentCompetencySchema = z.object({
    id: assessmentCompetencyIdSchema,
    label: z.string().trim().min(2).max(80),
    /** The score bands, house format "0-40: …; 41-70: …; 71-100: …". What a score is defended with. */
    anchors: z.string().trim().min(20).max(2000),
    /** Relative weight 1-100 in capability. */
    weight: z.number().int().min(1).max(100),
    /** library = copied from a vertical edition; custom = written for this requirement. */
    source: z.enum(["library", "custom"]),
    kind: z.string().max(40).optional(),
});
export const assessmentSetInputSchema = z
    .object({
    competencies: z.array(assessmentCompetencySchema).min(ASSESSMENT_LIMITS.min).max(ASSESSMENT_LIMITS.max),
    /** Why the criteria changed — recorded on the version and shown in its history. Required. */
    reason: z.string().trim().min(4, "Say why the criteria changed — it is recorded with the version").max(500),
})
    .superRefine((v, ctx) => {
    const seen = new Set();
    for (const c of v.competencies) {
        if (seen.has(c.id))
            ctx.addIssue({ code: "custom", message: `Competency "${c.id}" appears twice` });
        seen.add(c.id);
    }
});
/** A stable snake_case id for a custom competency label ("Business Central finance setup" → "business_central_finance_setup"). */
export function customCompetencyId(label) {
    const base = label
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[^\w\s]/g, " ")
        .trim()
        .replace(/\s+/g, "_")
        .replace(/^[^a-z]+/, "")
        .slice(0, 60);
    return base.length >= 2 ? base : `custom_${base || "competency"}`;
}
/**
 * Default score bands for a CUSTOM (domain) competency, in the house format, written so the
 * evaluator separates direct experience from transferable and generic knowledge — the distinction a
 * product/domain competency exists to make. A recruiter edits them; they are never hidden.
 */
export function customCompetencyAnchors(label) {
    const l = label.trim();
    return (`0-40: talks about ${l} only in general or theoretical terms, or describes it as something others did, ` +
        `and gives no account of their own work with it; ` +
        `41-70: describes their own comparable work — similar systems, processes or problems elsewhere — ` +
        `in enough detail to show what they did and how it turned out, or a concrete, correct approach to a ${l} task; ` +
        `71-100: describes their own direct work with ${l}, specifically, and at least two of: a decision they made and why, ` +
        `a problem they solved and how they knew it was solved, what goes wrong when it is done badly`);
}
// ── the evaluator's output ──────────────────────────────────────────────────
export const EVIDENCE_STRENGTHS = ["direct", "transferable", "weak"];
export const COMPETENCY_STATUSES = ["evidenced", "insufficient_evidence"];
/** What the reviewer might do next about ONE competency. Advice only — a human decides, and none of these
 *  is ever a rejection. Derived to agree with the status and score (see `actionOf`). */
export const COMPETENCY_ACTIONS = ["advance", "probe_further", "not_enough"];
/** Below this a competency is never "advance": the evidence shows the candidate is weak on it. */
export const ADVANCE_MIN_SCORE = 55;
export class EvaluationValidationError extends Error {
}
const clampScore = (n) => {
    if (typeof n !== "number" || !Number.isFinite(n))
        return null;
    return Math.max(0, Math.min(100, Math.round(n)));
};
const clamp01 = (n) => (typeof n === "number" && Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0);
/** Lower-case, collapse whitespace, unify quotes/dashes — so a span copied out of a voice transcript still matches. */
export function normaliseForSpan(s) {
    return s
        .toLowerCase()
        .normalize("NFKC")
        .replace(/[‘’ʼ`]/g, "'")
        .replace(/[“”]/g, '"')
        .replace(/[–—]/g, "-")
        .replace(/\s+/g, " ")
        .trim();
}
/** Does `span` occur in `text`? Tolerates the judge trimming a trailing/leading punctuation mark or ellipsis. */
export function spanOccursIn(span, text) {
    const s = normaliseForSpan(span).replace(/^[.…"'\s]+|[.…"'\s]+$/g, "");
    if (s.length < 3)
        return false;
    return normaliseForSpan(text).includes(s);
}
/** Minimum share of a quote's words the answer must contain, in order and close together. */
export const SPAN_MATCH_MIN = 0.8;
const wordOf = (w) => normaliseForSpan(w).replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, "");
/** Answer words a quote may skip inside the passage it was taken from, as a share of that passage. */
export const SPAN_MAX_SKIPPED = 0.15;
/**
 * Words that flip or hedge what a sentence claims. A quote must carry exactly the same ones as the
 * passage it came from: "I led the migration" is not a quote of "I never led the migration", and
 * "I configured it" is not a quote of "I would configure it" (audit E1 — the word alignment alone
 * accepted both, because a dropped word in the ANSWER cost nothing).
 */
const NEGATORS = new Set(["not", "no", "never", "nor", "none", "nothing", "nobody", "neither", "without", "cannot", "hardly", "barely"]);
const MODALS = new Set(["would", "could", "might", "may", "should", "will", "shall", "can", "must", "if", "unless", "maybe", "perhaps", "probably"]);
/** Contractions, with and without the apostrophe (speech-to-text drops it), → the modal they hedge. */
const NEG_CONTRACTIONS = {
    "don't": null, dont: null, "doesn't": null, doesnt: null, "didn't": null, didnt: null,
    "isn't": null, isnt: null, "wasn't": null, wasnt: null, "aren't": null, arent: null, "weren't": null, werent: null,
    "haven't": null, havent: null, "hasn't": null, hasnt: null, "hadn't": null, hadnt: null, "ain't": null, aint: null,
    "needn't": null, neednt: null, "can't": "can", cant: "can", "won't": "will", wont: "will", "wouldn't": "would",
    wouldnt: "would", "couldn't": "could", couldnt: "could", "shouldn't": "should", shouldnt: "should",
    "mustn't": "must", mustnt: "must", "mightn't": "might",
};
/**
 * Find the passage of `text` a quote was taken from, and return it VERBATIM.
 *
 * Models quote loosely — "Her CFO" for "Their CFO", two sentences stitched with a word dropped —
 * so an exact substring test discarded real evidence (live lab run, 4 Oct 2026: a clearly evidenced
 * competency downgraded to insufficient over one pronoun). This aligns the quote's words against the
 * answer's (local alignment: in order, small gaps allowed) and accepts it when at least
 * SPAN_MATCH_MIN of the quote's words are found in a passage not much longer than the quote. The
 * stored evidence is then the ANSWER's own words for that passage, never the model's paraphrase —
 * so every quote a recruiter reads is genuinely what the candidate said. Null = not in the answer.
 */
export function locateSpan(span, text) {
    const words = [...text.matchAll(/\S+/g)];
    const want = span.split(/\s+/).map(wordOf).filter(Boolean);
    // Word-aligned only (P1-1): a quote that starts or ends INSIDE a word ("ever led" out of "never led")
    // is not a quote of that passage. A character-level hit that is not a whole-word run falls through to
    // the alignment below, which cannot match a mangled word.
    let range = want.length ? wordRunOf(words, want) : null;
    if (!range)
        range = alignSpan(span, words);
    if (!range)
        return null;
    const found = text.slice(words[range.from].index, words[range.to].index + words[range.to][0].length);
    // Same meaning, not just the same words. The quote must carry the same negation and hedging as the
    // CLAUSE it was cut from — before it, after it, and across the commas speech-to-text scatters.
    const quotePol = polarityOf(span.split(/\s+/).map(wordOf));
    const clause = clauseAround(words, range.from, range.to);
    const clausePol = polarityOf(words.slice(clause.from, clause.to + 1).map((m) => wordOf(m[0])));
    if (quotePol.hard !== clausePol.hard)
        return null; // dropped/added negation or modal: the opposite claim
    if (quotePol.soft !== clausePol.soft) {
        // Only a hedge ("I think", "kind of") was left outside the quote: keep the evidence, but show the
        // candidate's whole clause so the recruiter reads the hedge.
        return text.slice(words[clause.from].index, words[clause.to].index + words[clause.to][0].length);
    }
    return found;
}
/** Hedges that soften a claim without reversing it. */
const HEDGE_SINGLE = new Set(["somewhat", "supposedly", "apparently", "allegedly", "roughly", "approximately", "sorta", "kinda"]);
const HEDGE_AFTER_I = new Set(["think", "guess", "suppose", "reckon", "believe", "imagine"]);
const SUBJECTS = new Set(["i", "we", "he", "she", "they", "it", "you", "my", "our", "then", "so", "also", "now"]);
const CLAUSE_BREAK = new Set(["but", "however", "though", "although", "whereas", "yet", "instead", "because", "since"]);
/** Words before / after a quote, inside its clause, that can still change what it claims. A negation
 *  further away than this belongs to a different part of the sentence ("…and found one approver with no delegate"). */
const LEAD_CAP = 5;
const TRAIL_CAP = 3;
function polarityOf(words) {
    const hard = [];
    const soft = [];
    for (let i = 0; i < words.length; i++) {
        const w = words[i];
        if (!w)
            continue;
        if (w in NEG_CONTRACTIONS) {
            hard.push("NEG");
            const modal = NEG_CONTRACTIONS[w];
            if (modal)
                hard.push(`MOD:${modal}`);
        }
        else if (NEGATORS.has(w)) {
            hard.push("NEG");
            if (w === "cannot")
                hard.push("MOD:can");
        }
        else if (MODALS.has(w)) {
            hard.push(`MOD:${w}`);
        }
        else if (HEDGE_SINGLE.has(w)) {
            soft.push(w);
        }
        else if (HEDGE_AFTER_I.has(w) && (words[i - 1] === "i" || words[i - 1] === "we")) {
            soft.push(`I ${w}`);
        }
        else if ((w === "kind" || w === "sort") && words[i + 1] === "of") {
            soft.push(`${w} of`);
        }
        else if (w === "more" && words[i + 1] === "or" && words[i + 2] === "less") {
            soft.push("more or less");
        }
    }
    return { hard: hard.sort().join(","), soft: soft.sort().join(",") };
}
/** Is the word at `i` where a new clause starts? (contrast words, "and I/we/they…") */
function startsClause(words, i) {
    const w = wordOf(words[i][0]);
    if (CLAUSE_BREAK.has(w))
        return true;
    // ", I led…" — a comma followed by a new subject starts a clause (a comma alone does not: STT scatters them).
    if (i > 0 && /,$/.test(words[i - 1][0]) && ["i", "we", "he", "she", "they"].includes(w))
        return true;
    if (w === "and") {
        const next = words[i + 1] ? wordOf(words[i + 1][0]) : "";
        return SUBJECTS.has(next);
    }
    return false;
}
const endsSentence = (m) => /[.!?…]["')\]]*$/.test(m[0]) || m[0].includes("\n");
/** The word range of the clause containing words [from, to]: bounded by sentence ends and clause words,
 *  NOT by commas (speech-to-text commas are arbitrary), and capped so one long run-on cannot swallow a quote. */
function clauseAround(words, from, to) {
    let a = from;
    if (!startsClause(words, from)) {
        for (let i = from - 1; i >= 0 && from - i <= LEAD_CAP; i--) {
            if (endsSentence(words[i]))
                break;
            if (startsClause(words, i))
                break;
            a = i;
        }
    }
    let b = to;
    if (!endsSentence(words[to])) {
        for (let i = to + 1; i < words.length && i - to <= TRAIL_CAP; i++) {
            if (startsClause(words, i))
                break;
            b = i;
            if (endsSentence(words[i]))
                break;
        }
    }
    return { from: a, to: b };
}
/** The word run of `want` in the answer's words, exactly (case/punctuation-insensitive), or null. */
function wordRunOf(words, want) {
    const t = words.map((m) => wordOf(m[0]));
    for (let s = 0; s + want.length <= t.length; s++) {
        let k = 0;
        while (k < want.length && t[s + k] === want[k])
            k++;
        if (k === want.length)
            return { from: s, to: s + want.length - 1 };
    }
    return null;
}
/** Word alignment of a loosely-copied quote against the answer (see locateSpan). */
function alignSpan(span, words) {
    const q = span.split(/\s+/).map(wordOf).filter(Boolean);
    if (q.length < 3)
        return null;
    const t = words.map((m) => wordOf(m[0]));
    // Smith-Waterman over words: match +2, mismatch -1, gap -1; free start and end in the answer.
    const n = q.length;
    const m = t.length;
    const H = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
    let best = 0;
    let bi = 0;
    let bj = 0;
    for (let i = 1; i <= n; i++) {
        for (let j = 1; j <= m; j++) {
            const diag = H[i - 1][j - 1] + (q[i - 1] === t[j - 1] && q[i - 1] !== "" ? 2 : -1);
            const v = Math.max(0, diag, H[i - 1][j] - 1, H[i][j - 1] - 1);
            H[i][j] = v;
            if (v > best) {
                best = v;
                bi = i;
                bj = j;
            }
        }
    }
    if (best === 0)
        return null;
    // Trace back to count matches and find where the passage starts.
    let i = bi;
    let j = bj;
    let matches = 0;
    while (i > 0 && j > 0 && H[i][j] > 0) {
        const h = H[i][j];
        if (h === H[i - 1][j - 1] + (q[i - 1] === t[j - 1] && q[i - 1] !== "" ? 2 : -1)) {
            if (q[i - 1] === t[j - 1])
                matches++;
            i--;
            j--;
        }
        else if (h === H[i - 1][j] - 1) {
            i--;
        }
        else {
            j--;
        }
    }
    const startWord = j; // first answer word of the passage (0-based)
    const endWord = bj - 1;
    const passageLen = endWord - startWord + 1;
    if (matches / n < SPAN_MATCH_MIN || passageLen > Math.ceil(n * 1.5) + 2)
        return null;
    // Answer words the quote silently left out (or replaced) — a long quote may drop one, never several.
    if (passageLen - matches > Math.max(1, Math.floor(passageLen * SPAN_MAX_SKIPPED)))
        return null;
    return { from: startWord, to: endWord };
}
/**
 * "I haven't worked on X" is an ADMISSION, not evidence of X (the Business Central incident, 4 Oct 2026:
 * a competency added after the interview came back scored 0 because the judge quoted the candidate
 * saying they had not done it). The prompt and the reply schema both say so; live runs of
 * gemini-flash-lite still quoted admissions about one run in three, so the rule is also enforced here.
 *
 * A span is an admission when it OPENS with a first-person negated-experience clause ("I haven't
 * directly worked with…", "we never used…") and then goes on to describe nothing of the candidate's own
 * (no contrast, no further first-person claim beyond more negation/hedging). A span that continues into
 * real work ("I haven't used BC but I rebuilt the approval flow in SAP") is kept: that part IS evidence.
 */
const ADMISSION_OPEN = /^(?:(?:honestly|actually|well|so|and|but|um|uh|yeah|see|to be honest)\s+)*(?:i|we)\s+(?:(?:have|had|do|did|would|will)\s+)?(?:not|never|haven't|havent|hadn't|hadnt|didn't|didnt|don't|dont)\b(.*)$/;
const EXPERIENCE_VERB = /\b(work(?:ed|ing)?|done|do|used?|using|had|have|has|configur\w*|implement\w*|handl\w*|manag\w*|exposure|experience|touch\w*|deal\w*|built|build|encounter\w*|set ?up|run|ran|lead|led)\b/;
const CONTINUES_INTO_WORK = /\b(but|however|although|though|instead|whereas|yet)\b|\b(?:i|we)\s+(?!would|wouldn'?t|will|won'?t|can'?t|cannot|don'?t|do not|didn'?t|did not|haven'?t|have not|never|am not|'m not|think|guess|mean|know|say|want|wish|hope|prefer)[a-z]+/;
export function isAdmissionOfNoExperience(span) {
    const t = normaliseForSpan(span).replace(/[^\p{L}\p{N}'\s]/gu, " ").replace(/\s+/g, " ").trim();
    const m = ADMISSION_OPEN.exec(t);
    if (!m)
        return false;
    const rest = m[1] ?? "";
    if (!EXPERIENCE_VERB.test(rest.split(" ").slice(0, 10).join(" ")))
        return false; // "I never skip the checklist" is a practice, not an admission
    return !CONTINUES_INTO_WORK.test(rest);
}
const STRENGTH_RANK = { none: 0, weak: 1, transferable: 2, direct: 3 };
/**
 * The competency's action label, forced to agree with what the evidence supports: no evidence is always
 * "not_enough"; a low score is never "advance"; an unknown label falls back to the safe reading of the score.
 * The model's advice line is kept (trimmed to two sentences' worth); a missing one gets a neutral default.
 */
function adviceOf(rawAction, rawAdvice, status, score, notes, id) {
    const fromScore = score !== null && score >= ADVANCE_MIN_SCORE ? "advance" : "probe_further";
    let action = COMPETENCY_ACTIONS.includes(rawAction)
        ? rawAction
        : status === "insufficient_evidence"
            ? "not_enough"
            : fromScore;
    if (status === "insufficient_evidence" && action !== "not_enough") {
        notes.push(`${id}: action "${action}" on a competency with no evidence — set to not_enough`);
        action = "not_enough";
    }
    else if (status === "evidenced" && action === "not_enough") {
        notes.push(`${id}: action not_enough on an evidenced competency — set from the score`);
        action = fromScore;
    }
    else if (action === "advance" && (score === null || score < ADVANCE_MIN_SCORE)) {
        notes.push(`${id}: action advance on a score below ${ADVANCE_MIN_SCORE} — set to probe_further`);
        action = "probe_further";
    }
    const advice = typeof rawAdvice === "string" ? rawAdvice.replace(/\s+/g, " ").trim().slice(0, 280) : "";
    return {
        action,
        advice: advice ||
            (action === "advance"
                ? "Evidence supports this competency."
                : action === "probe_further"
                    ? "Ask for a specific example of their own work on this."
                    : "This interview did not cover it — ask in a follow-up conversation."),
    };
}
/**
 * Validate and normalise the evaluator's answer against what it was asked. THROWS (→ the evaluation
 * fails closed, never becomes current) when the shape is wrong: a competency missing, duplicated or
 * unknown. SOFT-corrects what can be corrected honestly: unverifiable evidence is dropped, a score
 * without surviving evidence becomes insufficient_evidence, an insufficient competency's score is
 * nulled, scores and confidences are clamped. Every correction is noted.
 */
export function validateEvaluation(raw, set, answers) {
    const notes = [];
    if (!raw || !Array.isArray(raw.competencies)) {
        throw new EvaluationValidationError("Evaluator response is missing competencies");
    }
    const wanted = new Map(set.map((c) => [c.id, c]));
    const got = new Map();
    for (const c of raw.competencies) {
        if (!wanted.has(c.competencyId))
            throw new EvaluationValidationError(`Evaluator returned unknown competency "${c.competencyId}"`);
        if (got.has(c.competencyId))
            throw new EvaluationValidationError(`Evaluator returned competency "${c.competencyId}" twice`);
        got.set(c.competencyId, c);
    }
    const missing = set.filter((c) => !got.has(c.id)).map((c) => c.id);
    if (missing.length)
        throw new EvaluationValidationError(`Evaluator left out competencies: ${missing.join(", ")}`);
    const answerById = new Map(answers.map((a) => [a.questionId, a]));
    const byAnswer = new Map();
    const competencies = set.map((spec) => {
        const r = got.get(spec.id);
        const evidence = [];
        for (const e of Array.isArray(r.evidence) ? r.evidence : []) {
            const a = answerById.get(e.questionId);
            const strength = EVIDENCE_STRENGTHS.includes(e.strength) ? e.strength : null;
            if (!a || !a.answerText) {
                notes.push(`${spec.id}: evidence cites ${e.questionId}, which has no answer — dropped`);
                continue;
            }
            if (!strength) {
                notes.push(`${spec.id}: evidence strength "${e.strength}" is not direct/transferable/weak — dropped`);
                continue;
            }
            const verbatim = typeof e.span === "string" ? locateSpan(e.span, a.answerText) : null;
            if (!verbatim) {
                notes.push(`${spec.id}: quote not found in Q${a.seq} — dropped`);
                continue;
            }
            if (isAdmissionOfNoExperience(verbatim)) {
                notes.push(`${spec.id}: Q${a.seq} quote is an admission of no experience, not evidence — dropped`);
                continue;
            }
            if (normaliseForSpan(verbatim) !== normaliseForSpan(e.span))
                notes.push(`${spec.id}: Q${a.seq} quote replaced by the answer's own words`);
            evidence.push({ questionId: a.questionId, seq: a.seq, strength, span: verbatim.slice(0, 600) });
        }
        const level = evidence.reduce((best, e) => (STRENGTH_RANK[e.strength] > STRENGTH_RANK[best] ? e.strength : best), "none");
        let status = r.status === "evidenced" ? "evidenced" : "insufficient_evidence";
        let score = status === "evidenced" ? clampScore(r.score) : null;
        if (status === "evidenced" && evidence.length === 0) {
            notes.push(`${spec.id}: scored with no verifiable evidence — set to insufficient_evidence`);
            status = "insufficient_evidence";
            score = null;
        }
        if (status === "evidenced" && score === null) {
            notes.push(`${spec.id}: evidenced without a numeric score — set to insufficient_evidence`);
            status = "insufficient_evidence";
        }
        if (status === "insufficient_evidence" && evidence.length > 0) {
            // The evaluator found something but judged it not enough to score. Keep the quotes (the
            // recruiter sees what little there is) but never a number.
            score = null;
        }
        return {
            competencyId: spec.id,
            label: spec.label,
            weight: spec.weight,
            status,
            score,
            evidenceLevel: status === "evidenced" ? level : evidence.length ? level : "none",
            confidence: clamp01(r.confidence),
            summary: (typeof r.summary === "string" && r.summary.trim()) || (status === "evidenced" ? "Evidenced." : "No meaningful evidence in this interview."),
            ...adviceOf(r.action, r.advice, status, score, notes, spec.id),
            evidence,
        };
    });
    // The per-answer view is DERIVED from the competency results — the same quotes, the same strengths —
    // so an answer card can never say something the competency verdicts do not.
    for (const c of competencies) {
        for (const e of c.evidence) {
            const list = byAnswer.get(e.questionId) ?? byAnswer.set(e.questionId, []).get(e.questionId);
            if (!list.some((x) => x.competencyId === c.competencyId && x.span === e.span))
                list.push({ competencyId: c.competencyId, strength: e.strength, span: e.span });
        }
    }
    const translations = new Map((raw.translations ?? []).map((t) => [t.questionId, t.translationEn?.trim() || null]));
    const answerResults = answers.map((a) => {
        const ev = (byAnswer.get(a.questionId) ?? []).sort((x, y) => STRENGTH_RANK[y.strength] - STRENGTH_RANK[x.strength]);
        return {
            questionId: a.questionId,
            seq: a.seq,
            evidenceFor: [...new Set(ev.map((e) => e.competencyId))],
            evidence: ev,
            translationEn: a.answerText ? (translations.get(a.questionId) ?? null) : null,
        };
    });
    return {
        competencies,
        answers: answerResults,
        capability: capabilityOf(competencies),
        coverage: coverageOf(competencies),
        validationNotes: notes,
    };
}
// ── capability and coverage ─────────────────────────────────────────────────
/** Weighted mean of the EVIDENCED competencies' scores; null when nothing is evidenced. Insufficient
 *  evidence is never folded in as a zero. Two decimals, like the composite it replaces. */
export function capabilityOf(results) {
    let w = 0;
    let sum = 0;
    for (const r of results) {
        if (r.status !== "evidenced" || r.score === null)
            continue;
        const weight = r.weight > 0 ? r.weight : 1;
        w += weight;
        sum += weight * r.score;
    }
    // Whole points, like every score the recruiter sees — a 46.67 claims precision the evaluator never had.
    return w > 0 ? Math.round(sum / w) : null;
}
/**
 * PRODUCT DECISION (P1-1b, 5 Oct 2026): COVERAGE IS SUBSTANTIATED EVIDENCE.
 * A competency counts toward coverage only when its evidence includes something concrete — `direct`
 * (their own work) or `transferable` (their own comparable work). Weak-only evidence (a claim with
 * nothing behind it, generic or theoretical talk) is still READ and still scored against the rubric —
 * the rubric decides what such evidence is worth, normally the bottom of the competency — so it keeps
 * lowering capability when it is poor. What it can never do is make the interview look complete: a
 * fluent candidate who asserts every competency with nothing concrete has coverage ≈ 0 and reads as
 * "Limited evidence", never "Strong match". No score cap or band is applied; the rubric is untouched.
 */
export const SUBSTANTIATING_LEVELS = ["direct", "transferable"];
export const isSubstantiated = (r) => r.status === "evidenced" && SUBSTANTIATING_LEVELS.includes(r.evidenceLevel);
/** Share of the set's weight backed by concrete evidence (0..1, three decimals). */
export function coverageOf(results) {
    const total = results.reduce((a, r) => a + (r.weight > 0 ? r.weight : 1), 0);
    if (total === 0)
        return 0;
    const covered = results.reduce((a, r) => a + (isSubstantiated(r) ? (r.weight > 0 ? r.weight : 1) : 0), 0);
    return Math.round((covered / total) * 1000) / 1000;
}
export function coverageBreakdown(results) {
    const b = { total: results.length, evidenced: 0, direct: 0, transferable: 0, weak: 0, insufficient: 0, substantiated: 0 };
    for (const r of results) {
        if (r.status !== "evidenced") {
            b.insufficient++;
            continue;
        }
        b.evidenced++;
        if (isSubstantiated(r))
            b.substantiated++;
        if (r.evidenceLevel === "direct")
            b.direct++;
        else if (r.evidenceLevel === "transferable")
            b.transferable++;
        else
            b.weak++;
    }
    return b;
}
/** Coverage below this reads as "limited evidence" everywhere a capability score is shown. */
export const LIMITED_COVERAGE = 0.6;
/** The recruiter-facing line stored on the evaluation. Never a verdict: evidence for a human. */
export function evaluationSummaryLine(capability, coverage, b) {
    if (capability === null) {
        return `No competency had enough evidence to score (0 of ${b.total}). The interview did not collect evidence for these criteria — you decide what to do next.`;
    }
    const cov = `${b.evidenced} of ${b.total} competencies assessed, ${b.substantiated} backed by concrete evidence (${Math.round(coverage * 100)}% of the weight)`;
    const mix = [b.direct && `${b.direct} direct`, b.transferable && `${b.transferable} transferable`, b.weak && `${b.weak} weak`]
        .filter(Boolean)
        .join(", ");
    const limited = coverage < LIMITED_COVERAGE ? " Limited evidence — treat the capability score with care." : "";
    return `Capability ${Math.round(capability)}/100 on the evidenced competencies; ${cov}${mix ? ` — ${mix}` : ""}.${limited} A human decides.`;
}
export function matchClassOf(capability, coverage) {
    if (capability == null)
        return "no_evidence";
    if ((coverage ?? 0) < LIMITED_COVERAGE)
        return "limited_evidence";
    if (capability >= 70)
        return "strong";
    if (capability >= 40)
        return "moderate";
    return "low";
}
export const MATCH_CLASS_LABEL = {
    strong: "Strong match",
    moderate: "Moderate match",
    low: "Low match",
    limited_evidence: "Limited evidence",
    no_evidence: "No evidence",
};
export function rankTierOf(r) {
    if (r.basis === "interview") {
        if (!r.interview || r.interview.score == null)
            return 4;
        return interviewClassOf(r.interview) === "limited" ? 1 : 0;
    }
    if (!r.evaluated)
        return 4;
    if (r.outdated)
        return 3;
    if (r.capability == null)
        return 2;
    return (r.coverage ?? 0) >= LIMITED_COVERAGE ? 0 : 1;
}
/**
 * THE ONE PLACE a candidate's evaluation becomes a Rankable (P1-9). `evaluated` and `outdated` were
 * written inline in three API handlers with slightly different rules; every list, filter and API now
 * derives them here, from the evaluation's set version and the requirement's current one. The
 * organization's ranking basis and the candidate's interview score ride along, so the same function
 * orders every list whichever basis is switched on.
 */
export function rankableOf(i) {
    const evaluated = i.assessmentSetVersion != null;
    return {
        capability: i.capability,
        coverage: i.coverage,
        evaluated,
        outdated: evaluated && i.currentSetVersion != null && !isCurrentForSet(i.assessmentSetVersion, i.currentSetVersion),
        basis: i.basis ?? "competency",
        interview: i.interview ?? null,
        resume: i.resume ?? null,
        name: i.name ?? null,
        id: i.id ?? null,
    };
}
/**
 * Sort comparator — the ONLY ordering. Tier first, then the basis's own number, then the OTHER evaluation
 * as the tie-break (so two equal interview scores are separated by the competency score, and the reverse),
 * then the deterministic tie-breaks.
 */
export function compareForRanking(a, b) {
    const ta = rankTierOf(a);
    const tb = rankTierOf(b);
    if (ta !== tb)
        return ta - tb;
    const ia = a.interview?.score ?? -1;
    const ib = b.interview?.score ?? -1;
    const ca = a.capability ?? -1;
    const cb = b.capability ?? -1;
    const covA = a.coverage ?? 0;
    const covB = b.coverage ?? 0;
    if (a.basis === "interview") {
        if (ia !== ib)
            return ib - ia;
        if (ca !== cb)
            return cb - ca;
        if (covA !== covB)
            return covB - covA;
    }
    else {
        if (ca !== cb)
            return cb - ca;
        if (covA !== covB)
            return covB - covA;
        if (ia !== ib)
            return ib - ia;
    }
    const ra = a.resume ?? -1;
    const rb = b.resume ?? -1;
    if (ra !== rb)
        return rb - ra;
    const byName = (a.name ?? "").localeCompare(b.name ?? "");
    if (byName !== 0)
        return byName;
    return (a.id ?? "").localeCompare(b.id ?? "");
}
/** Rank any list: `rankable` maps an item to its Rankable. Returns a new array, best first. */
export function rankBy(items, rankable) {
    return [...items].sort((x, y) => compareForRanking(rankable(x), rankable(y)));
}
export function rankBucketOf(r) {
    if (r.basis === "interview") {
        if (!r.interview || r.interview.score == null)
            return "not_evaluated";
        switch (interviewClassOf(r.interview)) {
            case "strong":
                return "strong";
            case "mixed":
                return "moderate";
            case "weak":
                return "low";
            default:
                return "limited_evidence";
        }
    }
    if (!r.evaluated)
        return "not_evaluated";
    if (r.outdated)
        return "outdated";
    return matchClassOf(r.capability, r.coverage);
}
export const RANK_BUCKET_LABEL = {
    ...MATCH_CLASS_LABEL,
    outdated: "Out of date",
    not_evaluated: "Not evaluated",
};
/** Bucket labels when the list is ranked by the interview score. Same keys, answer-based wording. */
export const INTERVIEW_BUCKET_LABEL = {
    strong: INTERVIEW_CLASS_LABEL.strong,
    moderate: INTERVIEW_CLASS_LABEL.mixed,
    low: INTERVIEW_CLASS_LABEL.weak,
    limited_evidence: INTERVIEW_CLASS_LABEL.limited,
    no_evidence: INTERVIEW_CLASS_LABEL.none,
    outdated: "Out of date",
    not_evaluated: INTERVIEW_CLASS_LABEL.none,
};
/** The bucket label under the organization's ranking basis. */
export const rankBucketLabel = (bucket, basis = "competency") => (basis === "interview" ? INTERVIEW_BUCKET_LABEL : RANK_BUCKET_LABEL)[bucket];
// ── comparability ───────────────────────────────────────────────────────────
/** An evaluation is rankable against the requirement only when it was judged against the CURRENT
 *  assessment-set version. Anything else is shown, flagged "evaluated against vN", and not ranked. */
export function isCurrentForSet(evaluationSetVersion, currentSetVersion) {
    return evaluationSetVersion === currentSetVersion;
}
/**
 * Is `next` the same criteria as `prev` with only weights changed? Same competencies, and for each one
 * the same label, rubric text and source. A weight does not change what an answer evidences or what a
 * rubric says it is worth, so the existing competency results stay valid and only the derived numbers
 * (capability, coverage) move — recomputing them needs no model.
 */
export function isWeightOnlyChange(prev, next) {
    if (prev.length !== next.length)
        return false;
    const byId = new Map(prev.map((c) => [c.id, c]));
    return next.every((n) => {
        const p = byId.get(n.id);
        return !!p && p.label === n.label && p.anchors === n.anchors && p.source === n.source;
    });
}
/** Re-weight stored competency results and derive capability + coverage again — deterministic, no model. */
export function reweightCompetencies(results, weights) {
    const competencies = results.map((r) => ({ ...r, weight: weights.get(r.competencyId) ?? r.weight }));
    return { competencies, capability: capabilityOf(competencies), coverage: coverageOf(competencies) };
}
// ── evaluation mode (owner setting) ─────────────────────────────────────────
/**
 * isolated — one model call per competency: a competency's result never depends on what else is in the set
 *            (measured 5 Oct 2026: 0 unrelated-competency changes across 32 comparisons). THE DEFAULT.
 * single   — one call for the whole set: ~64% cheaper and ~30% faster, but competencies influence each other
 *            (measured: 14-16 of 32 unrelated statuses flipped, up to 15 points, when the set changed).
 */
export const EVALUATION_MODES = ["isolated", "single"];
/** platform_settings key; value `{ "mode": EvaluationMode }`. */
export const EVALUATION_MODE_SETTING_KEY = "evaluation_mode";
export const parseEvaluationMode = (v) => {
    const m = typeof v === "string" ? v : v?.mode;
    return m === "isolated" || m === "single" ? m : null;
};
//# sourceMappingURL=evaluation.js.map